import React, { useState } from 'react';
import { Box, IconButton, Typography, Link, Chip } from '@mui/material';
import { COLOR_PRIMARY_1_100, COLOR_PRIMARY_2, COLOR_TEXT_2 } from 'src/colors';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { exchangeRates } from 'src/const';
import { convertCurrency, formatCurrency } from 'src/utils/functions';
import LockIcon from '@mui/icons-material/Lock';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface SubmarketsDataGridProps {
  submarkets: any[];
  currency: string;
  subscriptionPlan: string;
  handleOnSampleClick: (plan?: string) => void;
  hoveredSubmarketId: string | null;
  setHoveredSubmarketId: (id: string | null) => void;
  handleHideSubmarket?: (submarketId: string) => void;
  showActions?: boolean;
  maxRows?: number;
  height?: string;
  onSortChange?: (sortedSubmarkets: any[]) => void;
}

const SubmarketsDataGrid: React.FC<SubmarketsDataGridProps> = ({
  submarkets,
  currency,
  subscriptionPlan,
  handleOnSampleClick,
  hoveredSubmarketId,
  setHoveredSubmarketId,
  handleHideSubmarket,
  showActions = true,
  maxRows,
  height = 'calc(100% - 60px)',
  onSortChange,
}) => {
  const userhasDataAccess = ['expert', 'scale'].includes(subscriptionPlan);
  const currencySymbol = exchangeRates[currency].symbol;
  const ROWS_PER_PAGE = maxRows || 100;

  // Limit submarkets if maxRows is specified
  const displaySubmarkets = maxRows ? submarkets.slice(0, maxRows) : submarkets;

  const columns: GridColDef[] = [
    {
      field: 'submarketName',
      headerName: 'Name',
      flex: 1,
      minWidth: 250,
      renderCell: params => (
        <Link
          target="_blank"
          href={params.row.submarketUrl}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            color: COLOR_PRIMARY_2,
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {params.value}
          <OpenInNewRoundedIcon fontSize="small" sx={{ opacity: 0.7 }} />
          {params.row.verified_tier == 'C' && <Chip label="Custom" color="success" size="small" />}
        </Link>
      ),
    },
    {
      field: 'supply',
      headerName: 'Active Listings',
      flex: 1,
      minWidth: 150,
      renderCell: params => {
        const value = params.value ? params.value : '-';
        return !userhasDataAccess ? (
          <IconButton onClick={() => handleOnSampleClick('expert')}>
            <LockIcon color="primary" />
          </IconButton>
        ) : (
          <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
        );
      },
    },
    {
      field: 'occupancy',
      headerName: 'Occupancy Rate',
      flex: 1,
      minWidth: 150,
      renderCell: params => {
        const value = params.value ? `${params.value}%` : '-';
        return !userhasDataAccess ? (
          <IconButton onClick={() => handleOnSampleClick('expert')}>
            <LockIcon color="primary" />
          </IconButton>
        ) : (
          <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
        );
      },
    },
    {
      field: 'adr',
      headerName: 'Daily Rate',
      flex: 1,
      minWidth: 150,
      renderCell: params => {
        const value = params.value
          ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, params.value)))}`
          : '-';
        return !userhasDataAccess ? (
          <IconButton onClick={() => handleOnSampleClick('expert')}>
            <LockIcon color="primary" />
          </IconButton>
        ) : (
          <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
        );
      },
    },
    {
      field: 'revenue',
      headerName: 'Revenue Potential',
      flex: 1,
      minWidth: 150,
      renderCell: params => {
        const value = params.value
          ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, params.value)))}`
          : '-';
        return !userhasDataAccess ? (
          <IconButton onClick={() => handleOnSampleClick('expert')}>
            <LockIcon color="primary" />
          </IconButton>
        ) : (
          <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
        );
      },
    },
    ...(showActions && handleHideSubmarket
      ? [
          {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            renderCell: params => {
              return (
                <IconButton
                  onClick={() => handleHideSubmarket(params.row.submarketId)}
                  sx={{ color: COLOR_TEXT_2 }}
                >
                  <VisibilityOffIcon />
                </IconButton>
              );
            },
          },
        ]
      : []),
  ];

  const handleSortChange = (model: any) => {
    if (!submarkets || !onSortChange) return;

    const sortedSubmarkets = [...submarkets].sort((a, b) => {
      const sortField = model[0]?.field;
      const sortDirection = model[0]?.sort === 'desc' ? -1 : 1;

      if (!sortField) return 0;

      // Handle numeric fields
      if (['occupancy', 'adr', 'revenue', 'supply'].includes(sortField)) {
        return (a[sortField] - b[sortField]) * sortDirection;
      }

      // Handle string fields
      if (sortField === 'submarketName') {
        return a[sortField].localeCompare(b[sortField]) * sortDirection;
      }

      return 0;
    });

    onSortChange(sortedSubmarkets);
  };

  if (!displaySubmarkets || displaySubmarkets.length === 0) {
    return (
      <Box
        display={'flex'}
        justifyContent="center"
        alignItems="center"
        sx={{ height: '200px', width: '100%' }}
      >
        <Typography variant="body1" color="gray">
          No Submarkets available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      <DataGrid
        rows={displaySubmarkets}
        columns={columns}
        disableSelectionOnClick
        getRowId={row => row.submarketId}
        pageSize={ROWS_PER_PAGE}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
        onSortModelChange={onSortChange ? handleSortChange : undefined}
        getRowClassName={params => {
          return params.row.submarketId === hoveredSubmarketId ? 'highlighted-row' : '';
        }}
        componentsProps={{
          row: {
            onMouseEnter: e => {
              const id = e.currentTarget.getAttribute('data-id');
              id && setHoveredSubmarketId(id);
            },
            onMouseLeave: () => {
              setHoveredSubmarketId(null);
            },
          },
        }}
        sx={{
          backgroundColor: 'white',
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& .highlighted-row': {
            backgroundColor: `${COLOR_PRIMARY_1_100} !important`,
          },
          '& .MuiDataGrid-cell': {
            fontWeight: 500,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 500,
          },
        }}
      />
    </Box>
  );
};

export default SubmarketsDataGrid;
